/**
 * useLinkTracking
 *
 * One delegated click listener that tracks every meaningful link in the SPA:
 * phone, email, outbound, social, file download, and clicks toward a conversion page.
 *
 * @module hooks/useLinkTracking
 *
 * Why delegated rather than per-link handlers: a coverage audit (2026-07-26) found ~25
 * raw `mailto:` anchors, several `tel:` anchors, and ~51 internal links pointing at
 * /contact or /book, almost none of them tracked. Instrumenting each site individually
 * would have meant touching twenty-odd files and would silently regress the first time
 * anyone added a new CTA. A single listener on the document cannot be forgotten.
 *
 * Anything that already tracks itself — TrackedLink, TrackedButton — marks itself with
 * `data-mb-tracked` and is skipped here, so nothing is counted twice.
 */

import { useEffect } from "react";
import {
  trackPhoneClick,
  trackEmailClick,
  trackSocialClick,
  trackOutboundLink,
  trackDownload,
  trackCTAClick,
} from "@/lib/analytics";

/** Hostname fragment → platform label for named social events. */
const SOCIAL_HOSTS: Record<string, string> = {
  "instagram.com": "Instagram",
  "linkedin.com": "LinkedIn",
  "kick.com": "Kick",
  "youtube.com": "YouTube",
  "youtu.be": "YouTube",
  "facebook.com": "Facebook",
  "x.com": "X",
  "twitter.com": "X",
  "tiktok.com": "TikTok",
  "github.com": "GitHub",
};

/** Paths that represent a conversion step, so a click toward one is a CTA. */
const CONVERSION_PATHS = [
  "/contact",
  "/book",
  "/es/contacto",
  "/es/agendar",
  "/google-check",
  // The Spanish route is /es/revision-google (routes.tsx) — a localized slug, not a
  // prefixed copy of the English path. Keep in sync when a conversion route is added.
  "/es/revision-google",
];

const DOWNLOAD_EXTENSIONS = [".pdf", ".doc", ".docx", ".xlsx", ".csv", ".zip"];

export const useLinkTracking = (language: "en" | "es" = "en"): void => {
  useEffect(() => {
    const onClick = (event: MouseEvent) => {
      // `auxclick` fires for ANY non-primary button, so a right-click (button 2) would
      // otherwise record a click the visitor never made — they were opening a context
      // menu. Only the middle button (1) actually opens the link.
      if (event.type === "auxclick" && event.button !== 1) return;

      const target = event.target;
      if (!(target instanceof Element)) return;

      const anchor = target.closest("a");
      if (!anchor) return;

      // Self-instrumented elements (TrackedLink, TrackedButton, marked CTAs) are skipped
      // for ordinary clicks — their own onClick fires, and handling them here would count
      // the click twice. But a React onClick never fires for auxclick, so on a
      // middle-click the marker means NOBODY is watching; the delegated listener must
      // handle it or middle-clicked marked CTAs record nothing.
      if (event.type !== "auxclick" && anchor.closest("[data-mb-tracked]")) return;

      const raw = anchor.getAttribute("href");
      if (!raw) return;

      if (raw.startsWith("tel:")) {
        trackPhoneClick(raw.slice(4));
        return;
      }
      if (raw.startsWith("mailto:")) {
        trackEmailClick(raw.slice(7).split("?")[0]);
        return;
      }
      // Ignore in-page anchors and non-navigational schemes.
      if (raw.startsWith("#") || raw.startsWith("javascript:")) return;

      let url: URL;
      try {
        url = new URL(anchor.href, window.location.href);
      } catch {
        return;
      }

      if (url.origin !== window.location.origin) {
        // Exact host or a dot-boundary subdomain — a bare endsWith would classify
        // notinstagram.com as Instagram.
        const host = Object.keys(SOCIAL_HOSTS).find(
          (h) => url.hostname === h || url.hostname.endsWith(`.${h}`)
        );
        if (host) trackSocialClick(SOCIAL_HOSTS[host], url.href);
        else trackOutboundLink(url.href);
        return;
      }

      const lowerPath = url.pathname.toLowerCase();
      if (DOWNLOAD_EXTENSIONS.some((ext) => lowerPath.endsWith(ext))) {
        trackDownload(url.pathname.split("/").pop() || url.pathname, url.pathname);
        return;
      }

      if (CONVERSION_PATHS.some((p) => url.pathname === p || url.pathname.startsWith(`${p}/`))) {
        const text = (anchor.textContent || "").replace(/\s+/g, " ").trim().slice(0, 100);
        trackCTAClick(text, url.pathname, language);
      }
    };

    // Capture phase: react-router's own handler calls preventDefault and re-renders, so
    // a bubble-phase listener can miss the click entirely on internal <Link>s.
    document.addEventListener("click", onClick, true);
    // Middle-click fires `auxclick`, never `click` (W3C UI Events). Without this, opening
    // a CTA, a PDF or an outbound link in a new tab recorded nothing.
    document.addEventListener("auxclick", onClick, true);
    return () => {
      document.removeEventListener("click", onClick, true);
      document.removeEventListener("auxclick", onClick, true);
    };
  }, [language]);
};

export default useLinkTracking;
